import React, { useContext, useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import SaveIcon from "@mui/icons-material/Save";
// IMPORTS
import { MuiTelInput } from "mui-tel-input";
import { UserContext } from "../../context/UserProvider";
import AccountsList from "./AccountsList";
import AccountSettings from "./AccountSettings";
import { GlassButtonLoading } from "../../themes/UiElements";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  gender: "",
  contactNumber: "",
  email: "",
  password: "",
};

// Common styling for all form fields to ensure a uniform, modern look
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
  },
  "& .MuiInputBase-input": {
    typography: "body1",
  },
  "& .MuiInputLabel-root": {
    typography: "body2",
    color: "text.secondary",
  },
};

export default function SettingsCard(props) {
  const [tab, setTab] = useState("myprofile");
  const [form, setForm] = useState(INITIAL_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [errors, setErrors] = useState({});

  const { user: userData } = useContext(UserContext);

  useEffect(() => {
    if (userData) {
      setForm({
        firstName: userData.firstName ?? "",
        lastName: userData.lastName ?? "",
        gender: userData.gender ?? "",
        contactNumber: userData.contactNumber ?? "",
        email: userData.email ?? "",
        password: "",
      });
    }
  }, [userData]);

  const handleTabChange = (_, newValue) => setTab(newValue);

  // Standard input handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Specific handler for MuiTelInput since it doesn't emit a standard event
  const handlePhoneChange = (newPhone) => {
    setForm((prev) => ({ ...prev, contactNumber: newPhone }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.email.trim()) newErrors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = "Enter a valid email address.";

    // Password validation is only applied if they are trying to type a new one
    if (form.password.trim() && form.password.length < 6)
      newErrors.password = "Password must be at least 6 characters.";

    return newErrors;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFeedback(null);

    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {};

    if (form.firstName.trim()) {
      payload.firstName = form.firstName.trim();
    }

    if (form.lastName.trim()) {
      payload.lastName = form.lastName.trim();
    }

    if (form.gender !== undefined) {
      payload.gender = form.gender;
    }

    // Remove spaces added by MuiTelInput
    if (form.contactNumber.trim()) {
      payload.contactNumber = form.contactNumber.replace(/\s+/g, "");
    }

    try {
      setSaving(true);

      const res = await axios.put(
        `${API_ENDPOINT}/user/update-profile`,
        payload,
        getHeader()
      );

      setFeedback({
        type: "success",
        message: res.data?.message || "Profile updated successfully.",
      });
    } catch (err) {
      setFeedback({
        type: "error",
        message:
          err.response?.data?.message ||
          err.response?.data?.errors?.join(", ") ||
          err.message ||
          "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 3,
        boxShadow: "0 2px 12px 0 rgba(0,0,0,0.05)",
      }}
    >
      <Tabs
        value={tab}
        onChange={handleTabChange}
        textColor="primary"
        indicatorColor="primary"
        sx={{ px: 2, pt: 1 }}
      >
        <Tab
          value="myprofile"
          label="My Profile"
          sx={{ textTransform: "none", fontWeight: 600 }}
        />
        <Tab
          value="accountslist"
          label="Accounts List"
          sx={{ textTransform: "none", fontWeight: 600 }}
        />
        <Tab
          value="settings"
          label="Settings"
          sx={{ textTransform: "none", fontWeight: 600 }}
        />
      </Tabs>

      <Divider />

      <CardContent sx={{ p: { xs: 2, md: 4 } }}>
        {tab === "myprofile" && (
          <Box component="form" onSubmit={handleSave} noValidate>
            {feedback && (
              <Alert
                severity={feedback.type}
                sx={{ mb: 4, borderRadius: 2 }}
                onClose={() => setFeedback(null)}
              >
                {feedback.message}
              </Alert>
            )}

            <Grid container columnSpacing={4} rowSpacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Gender"
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                  sx={textFieldSx}
                >
                  <MenuItem value="">
                    <em>Prefer not to say</em>
                  </MenuItem>
                  {GENDER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <MuiTelInput
                  fullWidth
                  defaultCountry="IN"
                  label="Contact Number"
                  name="contactNumber"
                  value={form.contactNumber}
                  onChange={handlePhoneChange}
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  disabled
                  type="email"
                  label="Email Address"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={
                    errors.password || "Leave blank to keep current password"
                  }
                  sx={textFieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" mt={4}>
              <GlassButtonLoading
                type="submit"
                variant="contained"
                disableElevation
                sx={{
                  px: 4,
                  py: 1.2,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: "none",
                }}
                loading={saving}
                disabled={saving}
              >
                Save Changes
              </GlassButtonLoading>
            </Box>
          </Box>
        )}

        {tab === "accountslist" && <AccountsList />}
        {tab === "settings" && <AccountSettings />}
      </CardContent>
    </Card>
  );
}
